import React, {useState, useEffect, useRef} from 'react';
import {useAuth} from '../context/AuthContext';
import {usePatient} from '../context/PatientContext';
import { readICDCodesFromLocalFile } from '../utils/readExcel';
import { authenticatedFetch, authenticatedFormPost } from '../utils/apiUtils';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Grid,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    CircularProgress,
    Chip,
    Autocomplete,
    Menu,
    MenuItem,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Alert,
    Snackbar,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ReactMarkdown from 'react-markdown';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import './Notes.css';

export const fetchNotes = async (userId, patientId, userObj) => {
    if (!userId || !patientId || !userObj) {
        console.error('Missing required parameters for fetchNotes');
        return [];
    }
    
    try {
        const data = await authenticatedFetch(`/api/get_notes/${userId}/${patientId}`, {}, userObj);
        return data.all_notes || [];
    } catch (error) {
        console.error('Error fetching notes:', error);
        throw error; // Re-throw to allow component to handle the error
    }
};

const Instructions = () => (
    <Paper elevation={3} sx={{p: 3, mb: 3, width: '100%'}}>
        <Typography variant="body1">
            Create, manage, and store clinical notes. You can even generate a structured SOAP or DAP note from your
            clinical note.
        </Typography>
    </Paper>
);

const Notes = () => {
    const {user} = useAuth();
    const {selectedPatient} = usePatient();
    const [allNotes, setAllNotes] = useState([]);
    const [clinicalText, setClinicalText] = useState('');
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [primaryDiagnosis, setPrimaryDiagnosis] = useState(null);
    const [secondaryDiagnosis, setSecondaryDiagnosis] = useState(null);
    const [tertiaryDiagnosis, setTertiaryDiagnosis] = useState(null);
    const [diagnosesList, setDiagnosesList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [conversionType, setConversionType] = useState('');
    const [anchorEl, setAnchorEl] = useState(null);
    const [editableConvertedNote, setEditableConvertedNote] = useState('');
    const [showConvertedNote, setShowConvertedNote] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [documentDate, setDocumentDate] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploads, setUploads] = useState([]);
    const fileInputRef = useRef(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [fetchError, setFetchError] = useState(null);

    // State for editing and deleting notes
    const [editingNoteId, setEditingNoteId] = useState(null);
    const [editingNoteType, setEditingNoteType] = useState(null);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null); // For note actions menu
    const [currentNoteMenu, setCurrentNoteMenu] = useState(null); // Note associated with open menu
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [noteToDelete, setNoteToDelete] = useState(null);
    const noteFormRef = useRef(null); // Ref for scrolling

    useEffect(() => {
        if (user && selectedPatient) {
            const loadNotes = async () => {
                try {
                    setFetchError(null);
                    const fetchedNotes = await fetchNotes(user.uid, selectedPatient.id, user);
                    setAllNotes(fetchedNotes);
                } catch (error) {
                    console.error('Error loading notes:', error);
                    setFetchError(error.message);
                    setAllNotes([]);
                    setSnackbar({
                        open: true,
                        message: `Failed to load notes: ${error.message}`,
                        severity: 'error'
                    });
                }
            };
            loadNotes();
        } else {
            setAllNotes([]); // Clear notes when no patient is selected
        }
    }, [user, selectedPatient]);

    useEffect(() => {
        const fetchDiagnoses = async () => {
            const data = await readICDCodesFromLocalFile();
            setDiagnosesList(data || []);
        };

        fetchDiagnoses();
    }, []);

    useEffect(() => {
        if (user && selectedPatient) {
            const fetchUploads = async () => {
                try {
                    setFetchError(null);
                    const data = await authenticatedFetch(`/api/get_uploads/${user.uid}/${selectedPatient.id}`, {}, user);
                    setUploads(data.uploads || []);
                } catch (error) {
                    console.error('Error fetching uploads:', error);
                    setFetchError(error.message);
                    setUploads([]);
                    setSnackbar({
                        open: true,
                        message: `Failed to load uploads: ${error.message}`,
                        severity: 'error'
                    });
                }
            };
            fetchUploads();
        } else {
            setUploads([]);
        }
    }, [user, selectedPatient]);

    const resetForm = () => {
        setClinicalText('');
        setStartTime('');
        setEndTime('');
        setPrimaryDiagnosis(null);
        setSecondaryDiagnosis(null);
        setTertiaryDiagnosis(null);
        setEditingNoteId(null);
        setEditingNoteType(null);
        setShowConvertedNote(false);
        setEditableConvertedNote('');
    };

    const handleConvertClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleConversionTypeSelect = (type) => {
        setConversionType(type);
        setAnchorEl(null);
        handleConvertNote(type);
    };

    const handleSnackbarClose = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // --- Note Action Menu Handlers ---
    const handleMenuOpen = (event, note) => {
        event.stopPropagation(); // Prevent Accordion toggle
        setMenuAnchorEl(event.currentTarget);
        setCurrentNoteMenu(note);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setCurrentNoteMenu(null);
    };

    const handleEditNote = () => {
        if (!currentNoteMenu) return;

        const noteToEdit = currentNoteMenu;
        setEditingNoteId(noteToEdit.id);
        setEditingNoteType(noteToEdit.note_type);

        // Populate form fields
        setClinicalText(noteToEdit.text || noteToEdit.SOAP_note || noteToEdit.DAP_note || '');
        setStartTime(noteToEdit.start_time ? noteToEdit.start_time.substring(0, 16) : ''); // Format for datetime-local
        setEndTime(noteToEdit.end_time ? noteToEdit.end_time.substring(0, 16) : '');     // Format for datetime-local

        // Find the diagnosis object from the list
        const findDiagnosis = (combined) => diagnosesList.find(d => d.combined === combined) || null;
        setPrimaryDiagnosis(findDiagnosis(noteToEdit.primary_diagnosis));
        setSecondaryDiagnosis(findDiagnosis(noteToEdit.secondary_diagnosis));
        setTertiaryDiagnosis(findDiagnosis(noteToEdit.tertiary_diagnosis));

        setShowConvertedNote(false); // Hide converted note section during edit
        handleMenuClose();

        // Scroll to the form
        noteFormRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleCancelEdit = () => {
        resetForm();
    };

    // --- Delete Handlers ---
    const handleDeleteRequest = () => {
        if (!currentNoteMenu) return;
        setNoteToDelete(currentNoteMenu);
        setDeleteConfirmOpen(true);
        handleMenuClose();
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmOpen(false);
        setNoteToDelete(null);
    };

    const handleDeleteConfirm = async () => {
        if (!noteToDelete || !user || !selectedPatient) return;

        setLoading(true); // Indicate loading during delete

        try {
            await authenticatedFetch(`/api/delete_note/${user.uid}/${selectedPatient.id}/${noteToDelete.id}/${noteToDelete.note_type}`, {
                method: 'DELETE',
            }, user);

            // Remove the note from the local state
            setAllNotes(prevNotes => prevNotes.filter(note => note.id !== noteToDelete.id));

            setSnackbar({
                open: true,
                message: 'Note deleted successfully!',
                severity: 'success'
            });

        } catch (error) {
            console.error('Error deleting note:', error);
            setSnackbar({
                open: true,
                message: `Failed to delete note: ${error.message}`,
                severity: 'error'
            });
        } finally {
            setDeleteConfirmOpen(false);
            setNoteToDelete(null);
            setLoading(false);
        }
    };

    const handleSaveNote = async (noteType) => {
        // Determine content based on whether it's a standard note or a converted one being saved
        const isConvertedSave = noteType === 'soap' || noteType === 'dap';
        const contentToSave = editingNoteId ? clinicalText : (isConvertedSave ? editableConvertedNote : clinicalText);
        const finalNoteType = editingNoteId ? editingNoteType : noteType;

        if (!contentToSave || contentToSave.trim() === '') {
            setSnackbar({ open: true, message: 'Note content cannot be empty.', severity: 'warning' });
            return;
        }

        setLoading(true); // Show loading indicator

        try {
            const noteData = {
                note_type: finalNoteType,
                note_content: contentToSave,
                start_time: startTime || null,
                end_time: endTime || null,
                primary_diagnosis: primaryDiagnosis?.combined || null,
                secondary_diagnosis: secondaryDiagnosis?.combined || null,
                tertiary_diagnosis: tertiaryDiagnosis?.combined || null,
            };

            // Determine URL and Method based on whether we are editing or creating
            const url = editingNoteId
                ? `/api/update_note/${user.uid}/${selectedPatient.id}/${editingNoteId}`
                : `/api/save_note/${user.uid}/${selectedPatient.id}`;
            const method = editingNoteId ? 'PUT' : 'POST';

            const result = await authenticatedFetch(url, {
                method: method,
                body: JSON.stringify(noteData),
            }, user);

            // Check for success based on response (id for create, message for update)
            if (result.id || (method === 'PUT' && result.message)) {

                if (editingNoteId) {
                    // Update existing note in state
                    setAllNotes(prevNotes => prevNotes.map(note =>
                        note.id === editingNoteId
                            ? { ...note, ...noteData, // Update with form data
                               text: finalNoteType === 'clinical' ? contentToSave : note.text,
                               SOAP_note: finalNoteType === 'soap' ? contentToSave : note.SOAP_note,
                               DAP_note: finalNoteType === 'dap' ? contentToSave : note.DAP_note,
                               timestamp: note.timestamp, // Keep original creation timestamp
                               last_updated: result.last_updated || new Date().toISOString()
                            }
                            : note
                    ));
                    setSnackbar({
                        open: true,
                        message: 'Note updated successfully!',
                        severity: 'success'
                    });

                } else {
                    // Add new note to state
                    const newNote = {
                        id: result.id,
                        [finalNoteType === 'clinical' ? 'text' : `${finalNoteType.toUpperCase()}_note`]: contentToSave,
                        start_time: startTime || null,
                        end_time: endTime || null,
                        primary_diagnosis: primaryDiagnosis?.combined || null,
                        secondary_diagnosis: secondaryDiagnosis?.combined || null,
                        tertiary_diagnosis: tertiaryDiagnosis?.combined || null,
                        timestamp: result.timestamp, // Use the timestamp returned by the server
                        note_type: finalNoteType
                    };
                    // Add the note to the beginning of the list and sort
                    setAllNotes(prevNotes => [
                       newNote,
                       ...prevNotes
                    ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))); // Ensure sort order

                    setSnackbar({
                        open: true,
                        message: 'Note saved successfully!',
                        severity: 'success'
                    });
                }

                resetForm(); // Reset form after successful save or update
            } else {
                throw new Error(result.error || 'Failed to save note');
            }
        } catch (error) {
            console.error('Error saving note:', error);
            setSnackbar({
                open: true,
                message: `${editingNoteId ? 'Updating' : 'Saving'} note failed: ${error.message}`,
                severity: 'error'
            });
        } finally {
            setLoading(false); // Hide loading indicator
        }
    };

    const handleConvertNote = async (type) => {
        if (!clinicalText || clinicalText.trim() === '') {
            setSnackbar({ open: true, message: 'Cannot convert an empty note.', severity: 'warning' });
            return;
        }
        setLoading(true);
        setEditableConvertedNote('');
        setShowConvertedNote(false);
        try {
            const result = await authenticatedFetch('/api/generate_insights_notes', {
                method: 'POST',
                body: JSON.stringify({
                    userId: user.uid,
                    patientId: selectedPatient.id,
                    note: clinicalText, // Use the main clinical text field for conversion
                    start_time: startTime || null,
                    end_time: endTime || null,
                    primaryDiagnosis: primaryDiagnosis?.combined || null,
                    secondaryDiagnosis: secondaryDiagnosis?.combined || null,
                    tertiaryDiagnosis: tertiaryDiagnosis?.combined || null,
                    conversionType: type,
                })
            }, user);

            if (result.error) {
                throw new Error(result.error);
            } else {
                setConversionType(type); // Store the type for saving later
                setShowConvertedNote(true);
                setEditableConvertedNote(result.converted_note);
                setEditingNoteId(null); // Cannot convert while editing
            }
        } catch (error) {
            console.error('Error converting note:', error);
            setSnackbar({
                open: true,
                message: `Failed to convert note: ${error.message}`,
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const formatDisplayTime = (note) => {
        // If session start time exists, use that
        if (note.start_time) {
            return new Date(note.start_time).toLocaleString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        }
        // Otherwise use the saved timestamp
        return new Date(note.timestamp).toLocaleString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleFileSelect = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };
    
    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Check if the file type is supported
            const fileType = file.name.split('.').pop().toLowerCase();
            if (['pdf', 'txt', 'docx'].includes(fileType)) {
                setSelectedFile(file);
                setShowUploadModal(true);
                // Clear the input to allow the same file to be selected again
                event.target.value = '';
            } else {
                alert('Please select a PDF, TXT, or DOCX file.');
                event.target.value = '';
            }
        }
    };
    
    const handleUploadCancel = () => {
        setShowUploadModal(false);
        setSelectedFile(null);
        setDocumentDate('');
    };
    
    const handleUploadSubmit = async () => {
        if (!selectedFile || !documentDate) {
            alert('Please select a file and enter an as of date.');
            return;
        }
        
        setUploading(true);
        
        try {
            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('userId', user.uid);
            formData.append('patientId', selectedPatient.id);
            formData.append('docDate', documentDate);
            
            const result = await authenticatedFormPost('/api/upload_document', formData, user);
            
            if (result) {
                // Success
                setShowUploadModal(false);
                setSelectedFile(null);
                setDocumentDate('');
                
                // Show success notification using Snackbar instead of alert
                setSnackbar({
                    open: true,
                    message: `File ${result.filename} uploaded successfully!`,
                    severity: 'success'
                });
                
                // Fetch the updated list of uploads
                try {
                    const uploadsData = await authenticatedFetch(`/api/get_uploads/${user.uid}/${selectedPatient.id}`, {}, user);
                    setUploads(uploadsData.uploads || []);
                } catch (error) {
                    console.error('Error refreshing uploads:', error);
                    setSnackbar({
                        open: true,
                        message: 'File uploaded but failed to refresh the list. Please reload the page.',
                        severity: 'warning'
                    });
                }
            } else {
                // Error
                throw new Error(result.error || 'Unknown upload error');
            }
        } catch (error) {
            console.error('Error uploading document:', error);
            setSnackbar({
                open: true,
                message: `Upload failed: ${error.message}`,
                severity: 'error'
            });
        } finally {
            setUploading(false);
        }
    };

    return (
        <Box sx={{
            width: '95%',
            maxWidth: '1500px',
            margin: '0 auto',
            padding: 2,
            mt: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
        }}>
            <Instructions/>
            
            {/* Error message if API calls fail */}
            {fetchError && (
                <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                    {fetchError}
                </Alert>
            )}
            
            <Paper ref={noteFormRef} elevation={3} sx={{
                p: 3,
                mb: 3,
                width: '100%',
                minHeight: '400px',
                display: 'flex',
                flexDirection: 'column',
            }}>
                <Typography variant="h5" gutterBottom sx={{mb: 3}}>
                    {editingNoteId ? 'Edit Note' : 'Create Note'}
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Session Start Time"
                            type="datetime-local"
                            value={startTime}
                            onChange={(e) => setStartTime(e.target.value)}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Session End Time"
                            type="datetime-local"
                            value={endTime}
                            onChange={(e) => setEndTime(e.target.value)}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Autocomplete
                            options={diagnosesList}
                            value={primaryDiagnosis}
                            getOptionLabel={(option) => option.combined || ''}
                            isOptionEqualToValue={(option, value) => option.code === value?.code}
                            renderInput={(params) => <TextField {...params} label="Primary Diagnosis" fullWidth/>}
                            onChange={(event, newValue) => {
                                setPrimaryDiagnosis(newValue);
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Autocomplete
                            options={diagnosesList}
                            value={secondaryDiagnosis}
                            getOptionLabel={(option) => option.combined || ''}
                            isOptionEqualToValue={(option, value) => option.code === value?.code}
                            renderInput={(params) => <TextField {...params} label="Secondary Diagnosis" fullWidth/>}
                            onChange={(event, newValue) => {
                                setSecondaryDiagnosis(newValue);
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Autocomplete
                            options={diagnosesList}
                            value={tertiaryDiagnosis}
                            getOptionLabel={(option) => option.combined || ''}
                            isOptionEqualToValue={(option, value) => option.code === value?.code}
                            renderInput={(params) => <TextField {...params} label="Tertiary Diagnosis" fullWidth/>}
                            onChange={(event, newValue) => {
                                setTertiaryDiagnosis(newValue);
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            rows={6}
                            variant="outlined"
                            label={editingNoteId ? `Editing ${editingNoteType?.toUpperCase()} Note` : "Enter Clinical Note"}
                            value={clinicalText}
                            onChange={(e) => setClinicalText(e.target.value)}
                            required
                        />
                    </Grid>
                </Grid>
                <Box sx={{mt: 2, display: 'flex', justifyContent: 'flex-start', flexWrap: 'wrap'}}>
                    <Button
                        onClick={() => handleSaveNote(editingNoteId ? editingNoteType : 'clinical')}
                        variant="contained"
                        color="primary"
                        disabled={!clinicalText || clinicalText.trim() === '' || loading}
                        sx={{mr: 2, mb: 1}}
                    >
                        {editingNoteId ? 'Save Changes' : 'Save Clinical Note'}
                    </Button>
                    {editingNoteId && (
                        <Button
                            onClick={handleCancelEdit}
                            variant="outlined"
                            color="secondary"
                            disabled={loading}
                            sx={{mr: 2, mb: 1}}
                        >
                            Cancel Edit
                        </Button>
                    )}
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleConvertClick}
                        disabled={!clinicalText || clinicalText.trim() === '' || editingNoteId || loading}
                        endIcon={<ArrowDropDownIcon/>}
                        sx={{mr: 2, mb: 1}}
                    >
                        Convert Note
                    </Button>
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handleFileSelect}
                        startIcon={<CloudUploadIcon />}
                        disabled={loading}
                        sx={{ mb: 1 }}
                    >
                        Upload Document
                    </Button>
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                        accept=".pdf,.txt,.docx"
                    />
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={() => setAnchorEl(null)}
                    >
                        <MenuItem onClick={() => handleConversionTypeSelect('SOAP')}>SOAP Note</MenuItem>
                        <MenuItem onClick={() => handleConversionTypeSelect('DAP')}>DAP Note</MenuItem>
                    </Menu>
                </Box>
            </Paper>

            {loading && (
                <Box sx={{display: 'flex', justifyContent: 'center', mt: 2, width: '100%'}}>
                    <CircularProgress/>
                </Box>
            )}

            {showConvertedNote && !editingNoteId && (
                <Paper elevation={3} sx={{
                    p: 3,
                    mb: 3,
                    width: '100%',
                    minHeight: '300px',
                    display: 'flex',
                    flexDirection: 'column',
                }}>
                    <Typography variant="h6" gutterBottom>{conversionType} Note</Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={10}
                        variant="outlined"
                        value={editableConvertedNote}
                        onChange={(e) => setEditableConvertedNote(e.target.value)}
                    />
                    <Button
                        onClick={() => handleSaveNote(conversionType.toLowerCase())}
                        variant="contained"
                        color="primary"
                        sx={{mt: 2, alignSelf: 'flex-end'}}
                        disabled={!editableConvertedNote || !editableConvertedNote.trim() || loading}
                    >
                        Save {conversionType} Note
                    </Button>
                </Paper>
            )}

            {/* File Upload Modal */}
            <Dialog open={showUploadModal} onClose={handleUploadCancel}>
                <DialogTitle>Upload Document</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Selected file: {selectedFile?.name}
                    </DialogContentText>
                    <TextField
                        margin="dense"
                        id="document-date"
                        label="As of Date (when the assessment/document was created)"
                        type="date"
                        fullWidth
                        variant="outlined"
                        value={documentDate}
                        onChange={(e) => setDocumentDate(e.target.value)}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        sx={{ mt: 2 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                        This date should reflect when the original document or assessment was created, not today's upload date.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleUploadCancel} color="primary">
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleUploadSubmit} 
                        color="primary"
                        disabled={uploading || !documentDate}
                    >
                        {uploading ? <CircularProgress size={24} /> : 'Upload'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Paper elevation={3} sx={{
                p: 3,
                width: '100%',
                minHeight: '300px',
                display: 'flex',
                flexDirection: 'column',
            }}>
                <Typography variant="h5" gutterBottom sx={{mb: 3}}>Notes History</Typography>
                {allNotes.length > 0 ? (
                    allNotes.map((note, index) => {
                        const noteContent = note.text || note.SOAP_note || note.DAP_note || "No content available";
                        const displayTime = formatDisplayTime(note);
                        const noteTypeDisplay = note.note_type ? note.note_type.toUpperCase() : 'Note';

                        return (
                            <Accordion key={note.id || index} sx={{mb: 2, width: '100%'}}>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon/>}
                                    aria-controls={`panel${index}-content`}
                                    id={`panel${index}-header`}
                                    sx={{
                                        '& .MuiAccordionSummary-content': {
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                        },
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, mr: 1 }}>
                                        <Chip
                                            label={displayTime}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                            sx={{mr: 1, flexShrink: 0 }}
                                        />
                                        {/* Conditionally render the type Chip if not clinical */}
                                        {noteTypeDisplay !== 'CLINICAL' && (
                                            <Chip
                                                label={noteTypeDisplay}
                                                size="small"
                                                variant="filled"
                                                sx={{ mr: 1, flexShrink: 0, backgroundColor: '#e0e0e0' }}
                                            />
                                        )}
                                        <Typography variant="body2" noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis', flexGrow: 1 }}>
                                            {noteContent.substring(0, 100)}...
                                        </Typography>
                                    </Box>
                                    <IconButton
                                        aria-label="note actions"
                                        size="small"
                                        onClick={(event) => handleMenuOpen(event, note)}
                                        sx={{ ml: 'auto', flexShrink: 0 }}
                                    >
                                        <MoreVertIcon />
                                    </IconButton>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <ReactMarkdown>{noteContent}</ReactMarkdown>
                                    {note.start_time && (
                                        <Typography variant="caption" display="block" sx={{ mt: 1 }}>Session Start
                                            Time: {new Date(note.start_time).toLocaleString()}</Typography>
                                    )}
                                    {note.end_time && (
                                        <Typography variant="caption" display="block" sx={{ mt: 0 }}>Session End
                                            Time: {new Date(note.end_time).toLocaleString()}</Typography>
                                    )}
                                    {note.primary_diagnosis && (
                                        <Chip label={`Primary: ${note.primary_diagnosis}`} sx={{mr: 1, mt: 1}} size="small"/>
                                    )}
                                    {note.secondary_diagnosis && (
                                        <Chip label={`Secondary: ${note.secondary_diagnosis}`} sx={{mr: 1, mt: 1}} size="small"/>
                                    )}
                                    {note.tertiary_diagnosis && (
                                        <Chip label={`Tertiary: ${note.tertiary_diagnosis}`} sx={{mr: 1, mt: 1}} size="small"/>
                                    )}
                                    <Typography variant="caption" color="textSecondary" display="block" sx={{mt: 2}}>
                                        Created: {new Date(note.timestamp).toLocaleString()}
                                        {note.last_updated && ` | Updated: ${new Date(note.last_updated).toLocaleString()}`}
                                    </Typography>
                                </AccordionDetails>
                            </Accordion>
                        );
                    })
                ) : fetchError ? (
                    <Typography
                        variant="body1"
                        sx={{
                            textAlign: 'center',
                            color: 'error.main',
                            mt: 4
                        }}
                    >
                        Unable to load notes. Please try again later.
                    </Typography>
                ) : (
                    <Typography
                        variant="body1"
                        sx={{
                            textAlign: 'center',
                            color: 'text.secondary',
                            mt: 4
                        }}
                    >
                        No notes have been created or saved yet.
                    </Typography>
                )}
            </Paper>
            
            {/* New Documents History section */}
            <Paper elevation={3} sx={{
                p: 3,
                width: '100%',
                minHeight: '300px',
                display: 'flex',
                flexDirection: 'column',
            }}>
                <Typography variant="h5" gutterBottom sx={{mb: 3}}>Documents History</Typography>
                {uploads.length > 0 ? (
                    uploads.map((upload, index) => (
                        <Accordion key={index} sx={{mb: 2, width: '100%'}}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon/>}
                                sx={{
                                    '& .MuiAccordionSummary-content': {
                                        display: 'flex',
                                        alignItems: 'center',
                                    },
                                }}
                            >
                                <Chip
                                    label={upload.filename}
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                    sx={{mr: 2}}
                                />
                                <Typography variant="caption" color="text.secondary">
                                    Document Date: {new Date(upload.document_date).toLocaleDateString()} | 
                                    Uploaded: {new Date(upload.timestamp).toLocaleString()}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography variant="body2" component="pre" sx={{
                                    whiteSpace: 'pre-wrap',
                                    fontFamily: 'monospace',
                                    fontSize: '0.875rem',
                                    backgroundColor: '#f5f5f5',
                                    padding: 2,
                                    borderRadius: 1,
                                    maxHeight: '400px',
                                    overflow: 'auto'
                                }}>
                                    {upload.content || upload.content_preview || "No content available"}
                                </Typography>
                            </AccordionDetails>
                        </Accordion>
                    ))
                ) : fetchError ? (
                    <Typography
                        variant="body1"
                        sx={{
                            textAlign: 'center',
                            color: 'error.main',
                            mt: 4
                        }}
                    >
                        Unable to load documents. Please try again later.
                    </Typography>
                ) : (
                    <Typography
                        variant="body1"
                        sx={{
                            textAlign: 'center',
                            color: 'text.secondary',
                            mt: 4
                        }}
                    >
                        No documents have been uploaded yet. Use the "Upload Document" button to upload your first document.
                    </Typography>
                )}
            </Paper>
            
            {/* Note Actions Menu */}
            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={handleEditNote}>Edit Note</MenuItem>
                <MenuItem onClick={handleDeleteRequest} sx={{ color: 'error.main' }}>Delete Note</MenuItem>
            </Menu>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{"Confirm Deletion"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to delete this note? This action cannot be undone.
                        {noteToDelete && <Typography variant="caption" display="block" sx={{mt:1}}>Note from: {formatDisplayTime(noteToDelete)}</Typography>}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="error" autoFocus disabled={loading}>
                        {loading ? <CircularProgress size={20} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar 
                open={snackbar.open} 
                autoHideDuration={6000} 
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={handleSnackbarClose} 
                    severity={snackbar.severity} 
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Notes;